import { useEffect, useMemo } from 'react'

import {
  ApplianceFeatures,
  ApplianceSettings,
  ApplianceType,
  CoaxOutputPort,
  CoaxPortMode,
  MatroxD4ApplianceSettings,
  MatroxE4ApplianceSettings,
  MatroxOutputPort,
  MatroxPortMode,
  MatroxS1ApplianceSettings,
  Output,
} from 'common/api/v1/types'
import { Select } from '../../../../common/Form'
import { CommonFields } from '../IpPortForm'
import MatroxSdiPortForm, { getMatroxSdiPortFormFields } from './MatroxSdiPortForm'
import VaSdiPortForm, { getVaSdiPortFormFields } from './VaSdiPortForm'
import { RichOption } from '../../../../common/Form/Select'
import { COAX_MODES } from '../../../../../utils'
import { useFormContext } from 'react-hook-form'
import { AllowUncontrolled } from 'src/components/common/Form/RHF'
import { isMatroxApplianceSettings } from 'common/matrox'

interface CoaxPortFormProps {
  namePrefix: string
  physicalPortIndex: string
  settings: ApplianceSettings | undefined
  applianceFeatures: ApplianceFeatures
  applianceType: ApplianceType
}

export const getCoaxPortFormFields = (port: CoaxOutputPort | MatroxOutputPort) => {
  const fields: string[] = [
    CommonFields.id,
    CommonFields.physicalPort,
    CommonFields.copies,
    CommonFields.mode,
    'encoderSettings',
  ]
  if (port.mode === CoaxPortMode.sdi) {
    fields.push(...getVaSdiPortFormFields())
  } else if (port.mode === MatroxPortMode.matroxSdi) {
    fields.push(...getMatroxSdiPortFormFields())
  }
  return fields
}

const CoaxPortForm = ({
  namePrefix,
  physicalPortIndex,
  settings,
  applianceFeatures,
  applianceType,
}: CoaxPortFormProps) => {
  const { getValues, setValue, watch } = useFormContext<Output & AllowUncontrolled>()
  const logicalPort: CoaxOutputPort | MatroxOutputPort = getValues(namePrefix)

  const modeKey = `${namePrefix}.mode`
  watch(modeKey)

  const selectedMode = logicalPort.mode
  const modeOptions: RichOption[] = useMemo(() => {
    const coaxModes = applianceFeatures.output?.modes.filter((m) => COAX_MODES.includes(m.mode)) ?? []
    return coaxModes.map((m) => ({ name: m.prettyName ?? m.mode, value: m.mode }))
  }, [JSON.stringify(applianceFeatures.output?.modes)])

  useEffect(() => {
    const defaultMode = modeOptions.length === 1 ? modeOptions[0].value : ''
    const isValidMode = modeOptions.some((m) => m.value === selectedMode)
    if (!isValidMode && selectedMode !== defaultMode) {
      setValue(modeKey, defaultMode)
    }
  }, [modeKey, setValue, modeOptions, selectedMode])

  return (
    <>
      <Select
        label="Mode"
        name={modeKey}
        required
        newLine
        options={modeOptions}
        validators={{
          oneOf: { validValues: new Set(modeOptions.filter((o) => !o.disabled).map(({ value }) => value)) },
        }}
      />

      {selectedMode === CoaxPortMode.sdi && <VaSdiPortForm namePrefix={namePrefix} />}
      {selectedMode === MatroxPortMode.matroxSdi && settings && isMatroxApplianceSettings(settings) && (
        <MatroxSdiPortForm
          namePrefix={namePrefix}
          physicalPortIndex={physicalPortIndex}
          port={logicalPort}
          applianceType={applianceType}
          settings={settings as MatroxD4ApplianceSettings | MatroxS1ApplianceSettings | MatroxE4ApplianceSettings}
        />
      )}
    </>
  )
}

export default CoaxPortForm
